import { Button, Grid, Popover, Stack, Typography } from "@mui/material";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import type { AnyRecord } from "@entrilia/shared/types";

interface Props {
  open: boolean;
  anchorEl: HTMLElement | undefined;
  isLoading: boolean;
  reportKind: "report" | "template";
  onSave: () => void;
  onContinue: () => void;
  onClose: () => void;
}

export default function UnsavedChangesPopover({
  open,
  anchorEl,
  onSave,
  onContinue,
  onClose,
  isLoading,
  reportKind,
}: Props) {
  const handleClose = (_: AnyRecord, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick" && isLoading) {
      return;
    }
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.32))",
            maxWidth: "310px",
            mt: 1,
            ml: -0.4,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              left: 0,
              width: 12,
              height: 12,
              bgcolor: "background.paper",
              transform: "translateY(-50%) translateX(100%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      }}
      sx={{
        minHeight: "45px",
      }}
    >
      <Stack spacing={1} useFlexGap sx={{ p: 2, width: "100%" }}>
        <Typography variant="subtitle1">Unsaved Changes</Typography>
        <Typography color="secondary">
          {`Please note that the data in this "${reportKind}" has not been saved. If you make changes to the "${reportKind}" visualization, all unsaved data will be lost.`}
        </Typography>
        <Grid container sx={{ gap: 1, pt: 1, width: "100%" }}>
          <HorizontalFill />
          <Button variant="outlined" onClick={onSave} loading={isLoading}>
            Save Changes
          </Button>
          <Button variant="contained" onClick={onContinue} disabled={isLoading}>
            Continue
          </Button>
        </Grid>
      </Stack>
    </Popover>
  );
}
